/**
 * /giveaway — the iPhone 18 Pro Max Giveaway review (migration 0506;
 * design/GIVEAWAY_PLAN.md). The period's entrants ranked the way the Official
 * Rules rank them, each with its breakdown, the active-days gate, and the
 * engagers the scorer excluded as indiscriminate, which staff can clear or
 * add to. The scorer runs hourly; Recompute runs it now.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { parseServerDate } from '@postopia/shared';
import { AdminShell } from '../components/AdminShell';
import { PageShell } from '../components/ui/PageShell';
import { giveawayApi, type GiveawayPeriodReview } from '../lib/giveawayApi';

export const Route = createFileRoute('/giveaway')({
  component: GiveawayRoute,
});

function GiveawayRoute() {
  return (
    <AdminShell mode="feature" featureName="Giveaway">
      <GiveawayConsole />
    </AdminShell>
  );
}

const cell: React.CSSProperties = { padding: '6px 8px', fontSize: 13, verticalAlign: 'top', borderBottom: '1px solid var(--c-border-hairline)' };
const head: React.CSSProperties = { ...cell, fontWeight: 600, color: 'var(--c-fg-secondary)', textAlign: 'left', fontSize: 12 };
const btn: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid var(--c-border-hairline)',
  background: 'var(--c-bg-surface)',
  color: 'var(--c-fg-default)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

function when(iso: string | null): string {
  if (!iso) return '';
  const d = parseServerDate(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso;
}

function GiveawayConsole() {
  const [period, setPeriod] = useState<1 | 2 | 3>(1);
  const [data, setData] = useState<GiveawayPeriodReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flagHandle, setFlagHandle] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await giveawayApi.period(period));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load.');
    }
  }, [period]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  const recompute = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = await giveawayApi.recompute();
      setNote(`Recomputed: ${r.entrants} entrants, ${r.flagged} flagged engagers, ${r.ms} ms.`);
      await load();
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Recompute failed.');
    } finally {
      setBusy(false);
    }
  };

  const setFlag = async (userId: string, on: boolean) => {
    setBusy(true);
    setNote(null);
    try {
      await giveawayApi.flag(period, userId, on, on ? 'staff review' : undefined);
      setNote(on ? 'Flagged; scores recomputed.' : 'Cleared; scores recomputed.');
      await load();
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  const flagByHandle = async () => {
    const h = flagHandle.trim().replace(/^@/, '');
    if (!h || !data) return;
    const row = data.rows.find((r) => r.handle === h) ?? null;
    // Engagers are not entrants: the flag takes a user id, so look it up in
    // the flags list (an auto flag) or the entrants; otherwise ask for the id.
    const id = row?.user_id ?? data.flags.find((f) => f.handle === h)?.user_id ?? (/^\d+$/.test(h) ? h : null);
    if (!id) {
      setNote('Enter a numeric user id for an account that is neither an entrant nor already flagged.');
      return;
    }
    await setFlag(id, true);
    setFlagHandle('');
  };

  return (
    <PageShell
      title="Giveaway"
      description="Entrants ranked as the Official Rules rank them. Scores are provisional until the period ends and a person reviews the leaders."
      maxWidth={1100}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {([1, 2, 3] as const).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setPeriod(n)}
            style={{ ...btn, background: period === n ? 'var(--c-fg-default)' : 'var(--c-bg-surface)', color: period === n ? 'var(--c-bg-page)' : 'var(--c-fg-default)' }}
          >
            Period {n}
          </button>
        ))}
        <button type="button" onClick={() => void recompute()} disabled={busy} style={{ ...btn, marginLeft: 'auto' }}>
          {busy ? 'Working…' : 'Recompute now'}
        </button>
      </div>
      {note ? <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 12px' }}>{note}</p> : null}
      {error ? (
        <p style={{ color: 'var(--c-fg-default)' }}>{error}</p>
      ) : !data ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>Loading…</p>
      ) : (
        <>
          <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 10px' }}>
            {when(data.period.startsAt)} to {when(data.period.endsAt)}, winner named {data.period.winnerOn}. {data.entrants_total} opted in;{' '}
            {data.rows.length} scored{data.rows[0]?.computed_at ? `, last run ${when(data.rows[0].computed_at)}` : ''}
            {data.rows[0]?.final ? ' (final)' : ' (provisional)'}.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={head}>#</th>
                  <th style={head}>Entrant</th>
                  <th style={head}>Score</th>
                  <th style={head}>Engagers</th>
                  <th style={head}>Active days</th>
                  <th style={head}>Reaction / Reply / Echo / Mention / Follow</th>
                  <th style={head}>Capped away</th>
                  <th style={head}>Reached</th>
                  <th style={head}>Opted in</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={r.user_id} style={{ opacity: r.eligible ? 1 : 0.6 }}>
                    <td style={cell}>{i + 1}</td>
                    <td style={cell}>
                      <strong>@{r.handle}</strong>
                      {r.display_name ? <span style={{ color: 'var(--c-fg-secondary)' }}> {r.display_name}</span> : null}
                      <div style={{ fontSize: 11, color: 'var(--c-fg-tertiary)' }}>
                        {r.country ?? '?'}{r.region ? `, ${r.region}` : ''}
                      </div>
                    </td>
                    <td style={{ ...cell, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{r.score}</td>
                    <td style={cell}>{r.distinct_engagers}</td>
                    <td style={cell}>
                      {r.eligible_days}
                      {!r.eligible ? <div style={{ fontSize: 11, color: 'var(--c-state-danger)' }}>not eligible yet</div> : null}
                    </td>
                    <td style={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>
                      {r.breakdown.reaction ?? 0} / {r.breakdown.reply ?? 0} / {r.breakdown.echo ?? 0} / {r.breakdown.mention ?? 0} / {r.breakdown.follow ?? 0}
                    </td>
                    <td style={cell}>{r.breakdown.capped_away ?? 0}</td>
                    <td style={cell}>{when(r.reached_at)}</td>
                    <td style={cell}>{when(r.opted_in_at)}</td>
                  </tr>
                ))}
                {data.rows.length === 0 ? (
                  <tr>
                    <td style={cell} colSpan={9}>No scores yet for this period.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <h2 style={{ fontSize: 15, fontWeight: 600, margin: '24px 0 6px' }}>Excluded engagers</h2>
          <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 10px' }}>
            Accounts whose engagement this period was not a choice (Official Rules, Section 4): echoed, reacted to or replied to most of
            what they were shown. Their engagement counts for nobody. Clear a false positive here, or flag an account by handle.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input
              value={flagHandle}
              onChange={(e) => setFlagHandle(e.target.value)}
              placeholder="@handle or user id to flag"
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--c-border-hairline)', background: 'var(--c-bg-surface)', color: 'var(--c-fg-default)', fontSize: 13, minWidth: 240 }}
            />
            <button type="button" onClick={() => void flagByHandle()} disabled={busy || !flagHandle.trim()} style={btn}>
              Flag
            </button>
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={head}>Account</th>
                <th style={head}>Why</th>
                <th style={head}>Engaged / Echoed / Shown</th>
                <th style={head}>Since</th>
                <th style={head}>Status</th>
                <th style={head}></th>
              </tr>
            </thead>
            <tbody>
              {data.flags.map((f) => (
                <tr key={f.user_id} style={{ opacity: f.cleared_at ? 0.55 : 1 }}>
                  <td style={cell}>@{f.handle}</td>
                  <td style={cell}>{f.source === 'staff' ? `staff: ${f.reason}` : f.reason.replaceAll('_', ' ')}</td>
                  <td style={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>{f.engaged_posts} / {f.echoed_posts} / {f.viewed_posts}</td>
                  <td style={cell}>{when(f.flagged_at)}</td>
                  <td style={cell}>{f.cleared_at ? `cleared by ${f.cleared_by ?? '?'}` : 'excluded'}</td>
                  <td style={cell}>
                    {f.cleared_at ? (
                      <button type="button" onClick={() => void setFlag(f.user_id, true)} disabled={busy} style={btn}>Flag again</button>
                    ) : (
                      <button type="button" onClick={() => void setFlag(f.user_id, false)} disabled={busy} style={btn}>Clear</button>
                    )}
                  </td>
                </tr>
              ))}
              {data.flags.length === 0 ? (
                <tr>
                  <td style={cell} colSpan={6}>No engagers flagged this period.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </>
      )}
    </PageShell>
  );
}
